import * as React from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import {
  Box,
  Button,
  Card,
  CardHeader,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PageHeader from "../../ui/PageHeader";
import StatCard from "../../ui/StatCard";
import EmptyState from "../../ui/EmptyState";
import ExpiryChip from "../../ui/ExpiryChip";
import { brand } from "../../theme";
import {
  emptyReportFilter,
  formatDate,
  moneyShort,
  premiumRow,
  sumBy,
} from "../../utils/premium";

const daysLeft = (date) => Math.max(0, moment(date).startOf("day").diff(moment().startOf("day"), "days"));

// Total premium per institution, biggest first; the tail folds into "Other"
function byInstitution(rows, top = 6) {
  const totals = {};
  rows.forEach((r) => {
    const key = r.institution || "Unknown";
    totals[key] = (totals[key] || 0) + (Number(r.cost) || 0);
  });
  const sorted = Object.entries(totals).sort((a, b) => b[1] - a[1]);
  if (sorted.length <= top) return sorted;
  const rest = sorted.slice(top - 1).reduce((acc, [, v]) => acc + v, 0);
  return [...sorted.slice(0, top - 1), ["Other", rest]];
}

export default function Analytics() {
  const navigate = useNavigate();
  const user = JSON.parse(secureLocalStorage.getItem("profile") || "null");
  const approved = useSelector((state) => state.approved);
  const monthly = useSelector((state) => state.monthly) || [];
  const loaded = useSelector((state) => state.loaded);
  const loading = !loaded.approved;
  const [filterData, setFilterData] = React.useState(emptyReportFilter);

  const rows = React.useMemo(
    () =>
      approved
        .filter((d) => moment(d.maturity_date) >= moment())
        .map((d) => premiumRow(d, filterData)),
    [approved, filterData]
  );
  const period =
    filterData.premium_charged_start && filterData.premium_charged_end
      ? `${formatDate(filterData.premium_charged_start)} – ${formatDate(filterData.premium_charged_end)}`
      : "To date";

  const expiringSoon = [...rows]
    .sort((a, b) => moment(a.maturity_date) - moment(b.maturity_date))
    .slice(0, 8);
  const institutions = byInstitution(rows);
  const maxInstitution = Math.max(1, ...institutions.map(([, v]) => v));

  const stats = [
    { label: "Active policies", value: rows.length.toLocaleString(), icon: <PolicyOutlinedIcon />, color: brand.slate },
    { label: "Total premium", value: moneyShort.format(sumBy(rows, "cost")), icon: <PaymentsOutlinedIcon />, color: brand.slate },
    { label: "Premium charged", value: moneyShort.format(sumBy(rows, "premium_charged")), hint: period, icon: <TrendingUpOutlinedIcon />, color: brand.greenDark },
    { label: "Premium uncharged", value: moneyShort.format(sumBy(rows, "premium_uncharged")), hint: period, icon: <HourglassBottomOutlinedIcon />, color: "#B45309" },
    { label: "Cumulative premium", value: moneyShort.format(sumBy(rows, "cumulative_premium")), hint: period, icon: <SavingsOutlinedIcon />, color: brand.slate },
  ];

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle={`Welcome back, ${user?.results?.firstName || ""}. Here is where the bank's insurance stands.`}
        actions={
          <>
            <TextField
              label="Charged from"
              type="date"
              value={filterData.premium_charged_start}
              onChange={(e) => setFilterData({ ...filterData, premium_charged_start: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 165 }}
            />
            <TextField
              label="Charged to"
              type="date"
              value={filterData.premium_charged_end}
              onChange={(e) => setFilterData({ ...filterData, premium_charged_end: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 165 }}
            />
            {(filterData.premium_charged_start || filterData.premium_charged_end) && (
              <Button onClick={() => setFilterData(emptyReportFilter)}>Clear</Button>
            )}
          </>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((s) => (
          <Grid item xs={6} lg key={s.label}>
            <StatCard {...s} loading={loading} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={7}>
          <Card sx={{ height: "100%" }}>
            <CardHeader
              title="Expiring soonest"
              subheader="Active policies by maturity date"
              titleTypographyProps={{ variant: "h6" }}
              subheaderTypographyProps={{ variant: "body2" }}
              action={
                <Button endIcon={<ArrowForwardIcon />} onClick={() => navigate("/insurance/report")}>
                  Full report
                </Button>
              }
            />
            <Divider />
            {loading ? (
              <Box sx={{ p: 2 }}>
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} height={36} />
                ))}
              </Box>
            ) : expiringSoon.length ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Policy</TableCell>
                      <TableCell>Institution</TableCell>
                      <TableCell align="right">Premium</TableCell>
                      <TableCell>Matures</TableCell>
                      <TableCell>Time left</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expiringSoon.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{r.description || r.policy_type}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.policy_number}
                          </Typography>
                        </TableCell>
                        <TableCell>{r.institution}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {moneyShort.format(r.cost)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(r.maturity_date)}</TableCell>
                        <TableCell>
                          <ExpiryChip days={daysLeft(r.maturity_date)} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <EmptyState title="No active policies" text="Approved policies that have not matured show here." />
            )}
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack gap={2.5} sx={{ height: "100%" }}>
            <Card>
              <CardHeader
                avatar={<EventBusyOutlinedIcon color="warning" />}
                title={
                  loaded.monthly
                    ? `${monthly.length} ${monthly.length === 1 ? "policy expires" : "policies expire"} this month`
                    : "Expiring this month"
                }
                titleTypographyProps={{ variant: "h6", fontSize: "1rem" }}
              />
              <Divider />
              {!loaded.monthly ? (
                <Box sx={{ p: 2 }}>
                  <Skeleton height={32} />
                  <Skeleton height={32} />
                </Box>
              ) : monthly.length ? (
                <List dense sx={{ maxHeight: 260, overflowY: "auto", py: 0 }}>
                  {monthly.map((m) => (
                    <ListItem key={m._id} divider secondaryAction={<ExpiryChip days={daysLeft(m.maturity_date)} />}>
                      <ListItemText
                        primary={m.description || m.policy_number}
                        secondary={`${m.institution || ""} · ${formatDate(m.maturity_date)}`}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: 13, noWrap: true }}
                        sx={{ pr: 10 }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  Nothing expires this month.
                </Typography>
              )}
            </Card>

            <Card sx={{ flex: 1 }}>
              <CardHeader
                title="Premium by institution"
                subheader="Active policies"
                titleTypographyProps={{ variant: "h6", fontSize: "1rem" }}
                subheaderTypographyProps={{ variant: "body2" }}
              />
              <Divider />
              <Stack gap={1.5} sx={{ p: 2 }}>
                {loading ? (
                  [...Array(4)].map((_, i) => <Skeleton key={i} height={28} />)
                ) : institutions.length ? (
                  institutions.map(([name, total]) => (
                    <Tooltip
                      key={name}
                      placement="top-start"
                      title={`${name}: ${moneyShort.format(total)}`}
                    >
                      <Box>
                        <Stack direction="row" justifyContent="space-between" gap={1} sx={{ mb: 0.5 }}>
                          <Typography sx={{ fontSize: 13 }} noWrap>
                            {name}
                          </Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                            {moneyShort.format(total)}
                          </Typography>
                        </Stack>
                        <Box sx={{ height: 8, borderRadius: 4, bgcolor: "#EEF1F3" }}>
                          <Box
                            sx={{
                              height: "100%",
                              width: `${(total / maxInstitution) * 100}%`,
                              minWidth: 4,
                              borderRadius: 4,
                              bgcolor: brand.slate,
                            }}
                          />
                        </Box>
                      </Box>
                    </Tooltip>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No data yet.
                  </Typography>
                )}
              </Stack>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
